import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiShare2, FiX, FiHeart, FiUsers } = FiIcons;

const SharePrompt = ({ results, calculationMode, onShare, onDismiss, isPwdOnly }) => {
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    if (results) {
      const hasSeenSharePrompt = localStorage.getItem('hasSeenSharePrompt');
      const calculationCount = parseInt(localStorage.getItem('calculationCount') || '0') + 1;
      localStorage.setItem('calculationCount', calculationCount.toString());

      if (!hasSeenSharePrompt && calculationCount >= 3) {
        const timer = setTimeout(() => {
          setShowPrompt(true);
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [results]);

  const handleShare = () => {
    setShowPrompt(false);
    localStorage.setItem('hasSeenSharePrompt', 'true');
    onShare();

    if (window.gtag) {
      window.gtag('event', 'share_prompt_accepted', {
        'event_category': 'engagement',
        'calculation_mode': calculationMode
      });
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('hasSeenSharePrompt', 'true');
    if (onDismiss) onDismiss();

    if (window.gtag) {
      window.gtag('event', 'share_prompt_dismissed', {
        'event_category': 'engagement',
        'calculation_mode': calculationMode
      });
    }
  };

  if (!showPrompt || !results) return null;

  const isGroupCalculation = calculationMode === 'group';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 50, scale: 0.9 }}
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl shadow-xl p-5 z-50"
      >
        <button 
          onClick={handleDismiss}
          className="absolute top-3 right-3 text-white/80 hover:text-white transition-colors"
          aria-label="Dismiss share prompt"
        >
          <SafeIcon icon={FiX} className="text-lg" />
        </button>

        <div className="flex items-center mb-3">
          <div className="bg-white/20 rounded-full p-2 mr-3">
            <SafeIcon icon={isGroupCalculation ? FiUsers : FiHeart} className="text-white text-xl" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">
              {isGroupCalculation ? 'Group Savings!' : 'Great Savings!'}
            </h3>
            <p className="text-green-100 text-sm">
              {isGroupCalculation 
                ? `Your group saved ₱${results.discount} total!` 
                : `You saved ₱${results.discount}!`}
            </p>
          </div>
        </div>

        <p className="text-white/90 text-sm mb-4">
          {isGroupCalculation 
            ? 'Help other groups calculate fair discounts by sharing this calculator!' 
            : `Help other ${isPwdOnly ? 'PWDs' : 'senior citizens and PWDs'} know their rights by sharing this calculator!`}
        </p>

        <div className="flex gap-3">
          <motion.button 
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleShare}
            className="flex-1 bg-white text-green-600 font-semibold rounded-lg py-2.5 px-4 hover:bg-green-50 transition-colors flex items-center justify-center"
          >
            <SafeIcon icon={FiShare2} className="mr-2" />
            Share Calculator
          </motion.button>
          
          <motion.button 
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleDismiss}
            className="px-4 py-2.5 text-white/80 hover:text-white transition-colors"
          >
            Maybe Later
          </motion.button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SharePrompt;